import { DownloadCloud02, ZoomIn, ZoomOut } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PdfIcon from 'src/assets/img/icons/filled-icons/PDFImgIcon.png';
import PngIcon from 'src/assets/img/icons/filled-icons/PNGImgIcon.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import TooltipCmp from 'src/components/TooltipCmp';
import { trackDocumentDownload } from 'src/services/DocumentsService';
import { downloadPdf, getDateWithSuffixFormat, getFormattedDate } from 'src/utils/CommonFunctions';

// Maps documentType enum values to display labels
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
    POD: 'POD',
    INVOICE: 'Invoice',
    BOL: 'BOL',
    CARRIER_BOL: 'Carrier BOL',
    CARRIER_INVOICE: 'Carrier invoice',
    RATE_CONFIRMATION: 'Rate confirmation',
    POP: 'POP',
    CUSTOMER_QUOTE: 'Customer quote',
    NEW_DOCUMENTS: 'New documents',
    CUSTOMER_INVOICE: 'Customer invoice',
    COMMERCIAL_INVOICE: 'Commercial invoice',
    OTHER: 'Other',
    PARS: 'PARS',
    SHIPMENT: 'Shipment',
    CUSTOMS_DOCUMENT: 'Customs document',
};

const ZOOM_LEVELS = [50, 75, 100, 125, 150];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];

const getFileExtensionType = (name?: string): string => {
    if (!name) return '';
    const parts = name.split('.');

    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
};

const getFileExtensionTypeImage = (extension: string): string => {
    const ext = extension?.toLowerCase();

    if (ext === 'png') return PngIcon;

    return PdfIcon; // default for pdf, jpg, jpeg, and everything else
};

interface IDocumentPreviewModalProps {
    document: any;
    handleClose: (status: boolean) => void;
}

const DocumentPreviewModal = ({ document, handleClose }: IDocumentPreviewModalProps) => {
    // WAL order id from the route (/orders/:id or /track/:id) for the portal usage event on download.
    const { id: routeOrderId } = useParams();
    const { t } = useTranslation();
    const [zoomLevel, setZoomLevel] = useState<number>(100);

    const fileExtension = getFileExtensionType(document?.file);
    const isPdf = fileExtension === 'pdf';
    const isImage = IMAGE_EXTENSIONS.includes(fileExtension);

    const fileUrl = document?.file && document?.fileUrl ? `${document.fileUrl}${document.file}` : '';

    // Cache-busting: appends updatedAt timestamp to the URL so the browser fetches the latest file
    // when a new version is uploaded under the same filename (bypasses browser cache)
    const previewUrl = fileUrl ? `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}t=${document?.updatedAt || Date.now()}` : '';

    const documentTypeLabel = DOCUMENT_TYPE_LABELS[document?.documentType] || document?.documentType || '';

    const handleZoomIn = () => {
        const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel);

        if (currentIndex < ZOOM_LEVELS.length - 1) {
            setZoomLevel(ZOOM_LEVELS[currentIndex + 1]);
        }
    };

    const handleZoomOut = () => {
        const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel);

        if (currentIndex > 0) {
            setZoomLevel(ZOOM_LEVELS[currentIndex - 1]);
        }
    };

    const handleDownload = () => {
        if (document?.fileUrl && document?.file) {
            const docFileUrl = `${document.fileUrl}${document.file}`;
            // Portal usage: report the download (fire-and-forget) with the document's type.
            trackDocumentDownload({ docType: String(document?.documentType || 'other').toLowerCase(), entityType: 'order', entityId: routeOrderId });
            downloadPdf(docFileUrl, document?.fileName || document?.file);
        }
    };

    return (
        <CommonModal title={documentTypeLabel} handleClose={handleClose} size="max-w-[980px] h-[85vh]" modalClassName="flex flex-col flex-1 overflow-hidden">
            <div className="flex flex-col flex-1 h-full overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-utilityGray200 bg-white800 flex-none">
                    <div className="flex items-center gap-2 min-w-0">
                        <img src={getFileExtensionTypeImage(fileExtension)} alt="icon" className="w-5 h-5 flex-none object-contain" />
                        <span className="text-sm font-semibold text-textSecondary truncate">{document?.fileName || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-none">
                        {(isPdf || isImage) && (
                            <>
                                <TooltipCmp message={t('Common.Zoom_In')}>
                                    <ButtonCmp size="none" icon={<ZoomIn className="w-4 h-4 text-grayLight600" />} onClick={handleZoomIn} disabled={zoomLevel >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}>
                                        <></>
                                    </ButtonCmp>
                                </TooltipCmp>
                                <TooltipCmp message={t('Common.Zoom_Out')}>
                                    <ButtonCmp size="none" icon={<ZoomOut className="w-4 h-4 text-grayLight600" />} onClick={handleZoomOut} disabled={zoomLevel <= ZOOM_LEVELS[0]}>
                                        <></>
                                    </ButtonCmp>
                                </TooltipCmp>
                                <div className="w-px h-4 bg-utilityGray200" />
                            </>
                        )}

                        <TooltipCmp message={t('Common.Download')}>
                            <ButtonCmp size="none" icon={<DownloadCloud02 className="w-4 h-4 text-grayLight600" />} onClick={handleDownload}>
                                <></>
                            </ButtonCmp>
                        </TooltipCmp>
                    </div>
                </div>

                {/* Preview Area */}
                <div className="flex-1 overflow-hidden pdf-preview-scrollbar bg-white800 p-4">
                    {isPdf && previewUrl && (
                        <iframe
                            src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                            className="w-full h-full border-0 rounded-lg bg-white"
                            style={{
                                transform: `scale(${zoomLevel / 100})`,
                                transformOrigin: 'top left',
                                width: `${10000 / zoomLevel}%`,
                                height: `${10000 / zoomLevel}%`,
                                colorScheme: '#f5f5f6',
                            }}
                            title={document?.fileName || t('Orders.Document_Preview')}
                        />
                    )}

                    {isImage && previewUrl && (
                        <div className="flex items-center justify-center h-full overflow-auto">
                            <img
                                src={previewUrl}
                                alt={document?.fileName || 'Document Preview'}
                                className="max-w-full object-contain rounded-lg"
                                style={{
                                    transform: `scale(${zoomLevel / 100})`,
                                    transformOrigin: 'center center',
                                }}
                            />
                        </div>
                    )}

                    {!isPdf && !isImage && (
                        <div className="flex flex-col items-center justify-center h-full gap-3">
                            <img src={getFileExtensionTypeImage(fileExtension)} alt="icon" className="w-12 h-12 object-contain" />
                            <p className="text-sm text-grayLight600">{t('Orders.Preview_Not_Available')}</p>
                            <ButtonCmp className="btn-outline-primary" icon={<DownloadCloud02 className="w-4 h-4" />} onClick={handleDownload}>
                                {t('Common.Download')}
                            </ButtonCmp>
                        </div>
                    )}
                </div>

                {/* Footer - document type + date */}
                <div className="px-4 py-2 flex-none bg-white800">
                    <p className="text-sm font-semibold text-textSecondary">{documentTypeLabel}</p>
                    {document?.createdAt && (
                        <p
                            className="text-xs leading-[18px] text-gray500 font-normal"
                            dangerouslySetInnerHTML={{
                                __html: getDateWithSuffixFormat(getFormattedDate(document.createdAt, 'MMMM D, YYYY', true, true)),
                            }}
                        />
                    )}
                </div>
            </div>
        </CommonModal>
    );
};

export default DocumentPreviewModal;
